import React, {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import qs from 'qs';
import { getAlpha } from 'utils/color';
import {
  selectCurrentCase,
  selectCurrentFolder,
  selectCurrentSelectedFileMetaData,
  selectIsGlobalPageLookup,
  selectIsLiveStream,
  selectMatchedUrl,
  selectPermissions,
  selectQuery,
  selectTheatreData,
  selectUserId,
  selectSessionStarted,
  selectFilteredDocumentAnnotations,
  selectMarkBasedScrolling,
  selectPresentQuery,
  selectHubConnectionId,
  selectBundleTabPage,
  selectCurrentCaseId,
  selectHardDisconnectOnlyHearingRoom,
  selectShowBundleTabPageWatermark,
  selectShowExternIdPageWatermark,
  selectShowGlobalPagePageWatermark,
  selectShowPrefixPlusGlobalPageWatermark,
  selectUseLegacyDownload,
} from 'common/selectors';
import * as actions from '../../redux/actions';
import { ZoomType } from '../types';
import { ViewerControl, MouseTool, Mark } from '@prizmdoc/viewer-core';
import AcusoftViewerToolbox from './AcusoftViewerToolbox';
import {
  getScrollContainer,
  makeMarkDisappear,
  setScroll,
  setSearchFunction,
  setMouse,
  zoom,
  getDocumentRotationInfo,
  createAnnotations,
  setOverflowHidden,
} from './utils';
import './index.scss';
import { openNewBackgroundTab } from 'utils/browser';
import { PAGENUMBER_MARK_TYPE, PRESENT_COLOR, SCROLL_MARK_TYPE } from './constants';
import { useSelector } from 'react-redux';
import { useSendMessages } from './hooks/useSendMessages';
import { useScrollToHighlight } from './hooks/useScrollToHighlight';
import { usePauseDocument } from './hooks/usePauseDocument';
import { usePerfectScrollbarUpdater } from './hooks/usePerfectScrollbarUpdater';
import { useFetchAnnotations } from 'features/viewing/redux/fetchAnnotations';
import { useFetchFileDetails } from 'features/case/redux/fetchFileDetails';
import NotionInterface from './NotionInterface';
import { useClearAnnotations } from 'features/viewing/redux/clearAnnotations';
import { useMarks } from './hooks/useMarks';
import debounce from 'utils/debounce';
import { diff } from 'deep-object-diff';
import { isEmpty } from 'utils/objects';
import { useDispatch } from 'react-redux';
import logger from 'utils/logger';
import { makeCancelable } from 'utils/promises';
import Split from 'react-split';
import classNames from 'classnames';
import Logger from 'utils/logger';
import SearchAllFiles from './SearchAllFiles';
import T from 'i18n';
import { usePresentView, safeGetJSON } from './hooks/usePresentView';
import { FileDownload } from 'features/case/DocumentsModal/modals/FileDownload';
import LegacyFileDownload from 'features/case/DocumentsModal/modals/LegacyFileDownload';
import { setCurrentSelectedFile as setCurrentSelectedFileAction } from 'features/case/redux/setCurrentSelectedFile';
import PresentViewClearSearchPopup from './PresentViewClearSearchPopup';
import RecentlyPresented from './RecentlyPresented';
import PausedBanner from './AcusoftViewerToolbox/PausedBanner';
import { getGlobalPageText, getStartPageForGlobalPaging } from './AcusoftViewerToolbox/globalPage';

const AcusoftViewerContainer = ({
  removeInput,
  enableScroll,
  className,
  options,
  viewerReadyHandler,
  pageOpeningHandler,
  pageDisplayedHandler,
  pageListElementRef,
  viewerControlRef,
}: any) => {
  return (
    <div
      // ACUSOFT CONTAINER
      style={{
        ...(removeInput && !enableScroll && { pointerEvents: 'none' }),
      }}
      data-pcc-pagelist
      className={className}
      ref={(el) => {
        if (el !== null && !pageListElementRef.current) {
          pageListElementRef.current = el;

          viewerControlRef.current = new ViewerControl(el, options);
          viewerControlRef.current.on('ViewerReady', viewerReadyHandler);
          viewerControlRef.current.on('PageOpening', pageOpeningHandler);
          viewerControlRef.current.on('PageDisplayed', pageDisplayedHandler);
        }
      }}
    />
  );
};

export default memo(
  forwardRef(
    (
      {
        fileId,
        removeInput,
        fileData,
        className,
        enableScroll,
        isFullScreen,
        isPreviewMode,
        options,
        viewingFilePrivate,
        passViewingSessionIdToReceivers,
        firstParentTriaBundleFileDetails,
        hearingRoomMode,
        searchHandler,
        searchFunctionsHandler,
        sideSummaryOpen,
        fetchViewingSessionIdHandler,
        isQueryParamMatchingAnyFolder,
        startPageForGlobalPaging,
        onViewerReady,
        hidePresentButton,
      }: any,
      ref,
    ) => {
      const fetchTheatreDataPending = useSelector(
        (state: any) => state.viewing.fetchTheatreDataPending,
      );
      const presentQuery = useSelector(selectPresentQuery);
      const annotationId = useSelector((state: any) => state.matcher.params.annotationId);
      const prevAnnotationIdRef = useRef(null);
      const connectionId = useSelector(selectHubConnectionId);
      const userId = useSelector(selectUserId);
      const permissions = useSelector(selectPermissions);
      const currentCase = useSelector(selectCurrentCase);
      const filteredAnnotations = useSelector(selectFilteredDocumentAnnotations);
      const isLiveStream = useSelector(selectIsLiveStream);
      const currentFolder = useSelector(selectCurrentFolder);
      const inHearingRoom = window.location.pathname.includes('/public-hearing-room/');
      const fileDataDetails = useMemo(
        () => (fileData?.currentFileDetails ? fileData.currentFileDetails : fileData),
        [fileData],
      );
      let currentFileMetaData = useSelector(selectCurrentSelectedFileMetaData) as any;
      if (inHearingRoom) currentFileMetaData = fileDataDetails;
      const normalizedFileMetaData = useMemo(() => {
        const src: any = (inHearingRoom ? fileDataDetails : currentFileMetaData) || {};
        const bl = (src.bundleLocations && src.bundleLocations[0]) || {};
        return {
          ...src,
          globalPagePrefix: src.globalPagePrefix || bl.globalPagePrefix,
          tab: src.tab || bl.tab,
          startPage: src.startPage ?? bl.startPage,
          folderFileId: src.folderFileId || bl.folderFileId,
          pageCount: src.pageCount || bl.pageCount || src.pageCount,
        };
      }, [fileDataDetails, inHearingRoom, currentFileMetaData]);
      if (!inHearingRoom) currentFileMetaData = normalizedFileMetaData;
      const hearingRoomStarted = useSelector(selectSessionStarted);
      const prevHearingRoomStartedRef = useRef(null);
      const isGlobalPageLookup = useSelector(selectIsGlobalPageLookup);
      const queryParam = useSelector(selectQuery);
      const currentUrl = useSelector(selectMatchedUrl);
      const btpActive = useSelector(selectBundleTabPage);
      const caseId = useSelector(selectCurrentCaseId);
      const theatreData = useSelector((state: any) =>
        selectTheatreData(state, hearingRoomMode),
      ) as any;
      const markBasedScrolling = useSelector(selectMarkBasedScrolling);
      const showBundleTabPageWatermark = useSelector(selectShowBundleTabPageWatermark);
      const showExternIdPageWatermark = useSelector(selectShowExternIdPageWatermark);
      const showGlobalPagePageWatermark = useSelector(selectShowGlobalPagePageWatermark);
      const showPrefixPlusGlobalPageWatermark = useSelector(
        selectShowPrefixPlusGlobalPageWatermark,
      );
      const useLegacyDownload = useSelector(selectUseLegacyDownload);
      const DownloadComponent = useLegacyDownload ? LegacyFileDownload : FileDownload;
      const [isSearching, setIsSearching] = useState(false);
      const [externalId, setExternalId] = useState<string | null>(null);

      const mapDocsWithSearchContext = useCallback((docs: any[], searchValue?: string) => {
        if (!Array.isArray(docs)) return [];
        if (!searchValue) return docs;
        return docs.map((doc) => {
          if (!doc || typeof doc !== 'object') return doc;
          const existingContext = (doc as any).searchContext;
          if (existingContext === searchValue) return doc;
          return { ...doc, searchContext: existingContext ?? searchValue };
        });
      }, []);

      const [docsToDisplay, setDocsToDisplay] = useState<any[]>(() => {
        // Initialize with restored data if available
        try {
          const storedDocsData = safeGetJSON(sessionStorage, `${caseId}.docsToDisplay`, null);
          if (storedDocsData) {
            if (Array.isArray(storedDocsData)) {
              return mapDocsWithSearchContext(storedDocsData);
            } else if (storedDocsData.docs && Array.isArray(storedDocsData.docs)) {
              return mapDocsWithSearchContext(storedDocsData.docs, storedDocsData.searchValue);
            }
          }
        } catch (error) {
          console.error('Failed to initialize docsToDisplay from storage:', error);
        }
        return [];
      });
      const [recentSearches, setRecentSearches] = useState<any>(() => {
        try {
          const storedSearches = localStorage.getItem(`${caseId}.recentSearches`);
          return storedSearches ? JSON.parse(storedSearches) : {};
        } catch (error) {
          console.error(error);
          return {};
        }
      });
      const [isViewerReady, setIsViewerReady] = useState<false | undefined | true | null>(
        undefined,
      );
      const viewerReadyNotifiedRef = useRef(false);
      const [selectedMouseTool, setSelectedMouseTool] = useState(MouseTool.Type.PanAndEdit);
      const [value, setValue] = useState<string>(() => {
        // Initialize with stored search value if available - check context storage first
        try {
          const storedDocsData = safeGetJSON(sessionStorage, `${caseId}.docsToDisplay`, null);
          if (storedDocsData && !Array.isArray(storedDocsData) && storedDocsData.searchValue) {
            return storedDocsData.searchValue;
          }
          // Fallback to separate storage
          const storedValue = safeGetJSON(sessionStorage, `${caseId}.searchValue`, '');
          if (storedValue) {
            return storedValue;
          }
        } catch (error) {
          console.error('Failed to initialize search value from storage:', error);
        }
        return '';
      });
      const [marksCreatedFirstTime, setMarksCreatedFirstTime] = useState(false);
      const [showFileVariantsModal, setShowFileVariantsModal] = useState<boolean>(false);
      const [selectedFileForDownload, setSelectedFileForDownload] = useState<any>(null);

      // Track request IDs to prevent race conditions in search
      const requestIdRef = useRef<number>(0);

      // Sync requestId from sessionStorage if available
      useEffect(() => {
        const syncRequestId = () => {
          try {
            const storedDocsData = safeGetJSON(sessionStorage, `${caseId}.docsToDisplay`, null);
            if (storedDocsData && !Array.isArray(storedDocsData) && storedDocsData.requestId) {
              requestIdRef.current = storedDocsData.requestId;
            }
          } catch (error) {
            console.error('Failed to sync requestId:', error);
          }
        };

        syncRequestId();
      }, []); // Only run once on mount

      function handleRemoveRecentSearch(e: React.MouseEvent, searchValue: string) {
        e.stopPropagation(); // Prevent clicking on the 'x' from also triggering handleOpenRecentlyPresented
        const updatedSearches = { ...recentSearches };
        delete updatedSearches[searchValue];
        setRecentSearches(updatedSearches);
        localStorage.setItem(`${caseId}.recentSearches`, JSON.stringify(updatedSearches));
      }

      const presenterConnectionId = useMemo(() => {
        return (
          (fileId &&
            (theatreData?.messages || []).find(
              (message: any) => message.fileId === fileId && userId === message.presenterId,
            )?.connectionId) ||
          null
        );
      }, [fileId, theatreData?.messages, userId]);
      const hasBeenPresenting = useRef(false);
      const showGlobalPaging = currentFolder && currentFolder.globalPaging;
      const isPresenting = !!presentQuery;
      const allHighlights = useMemo(
        () => (isPreviewMode || isPresenting ? [] : filteredAnnotations),
        [isPreviewMode, isPresenting, filteredAnnotations],
      );
      const prevHightlightsRef = useRef<any>([]);
      const isPresentModePage = currentUrl.includes(`${currentCase.id}/present-`);
      const isTheatreMode = !!hearingRoomMode;
      const showSidebar = !isTheatreMode && isFullScreen && !isPreviewMode;
      const [searchError, setSearchError] = useState('');

      const hardDisconnectOnlyHearingRoom =
        useSelector(selectHardDisconnectOnlyHearingRoom) && isTheatreMode;

      const [, updateState] = React.useState<any>();

      const forceUpdate = React.useCallback(() => updateState({}), []);

      const [hasClickedSummary, setHasClickedSummary] = useState(false);
      const pageListElementRef = useRef<any>(null);
      const viewerControlRef = useRef<any>();
      const initializingRef = useRef<number>(100000);
      const hideTimeoutRef = useRef<any>(null);
      const prevDoubleTimestampRef = useRef<number>(0);
      const prevTrippleTimestampRef = useRef<number>(0);
      const toolboxRef = useRef<any>(null);
      const pendingPageNumberRef = useRef<number | null>(null);
      const currentFileMetaDataValueRef = useRef<any>(null);
      const bundleTabPageMarkRef = useRef(null);
      const externIdPageMarkRef = useRef(null);
      const globalPageMarkRef = useRef(null);

      const pauseCreateMarkHandlerRef = useRef<any>(null);
      const pauseCreateMarkHandlerRef2 = useRef<any>(null);

      const { fetchAnnotations } = useFetchAnnotations();
      const { fetchFileDetails } = useFetchFileDetails();
      const { clearAnnotations } = useClearAnnotations();

      const updatePerfectScrollbar = usePerfectScrollbarUpdater(viewerControlRef);

      const {
        unpauseTheatreModeHandler,
        disconnectPauseTheatreModeHandler,
        pauseTheatreMode,
        isDisconnected,
      } = usePauseDocument(
        viewerControlRef.current,
        isTheatreMode,
        initializingRef,
        selectedMouseTool,
        hardDisconnectOnlyHearingRoom,
      );

      const [showClearAllDocsModal, setShowClearAllDocsModal] = useState(false);

      const {
        deleteMarkToBeCreated,
        setMarkToBeUpdated,
        markToBeCreated,
        markToBeUpdated,
        setMarkToBeCreated,
      } = useMarks(
        viewerControlRef.current,
        isPresenting,
        isTheatreMode,
        pauseCreateMarkHandlerRef,
        pauseCreateMarkHandlerRef2,
      );

      const setCurrentMouseTool = useCallback(
        (selMouseTool, isPresent?) => {
          deleteMarkToBeCreated();
          if (viewerControlRef?.current)
            setMouse(
              selMouseTool,
              isPresent !== undefined ? isPresent : isPresenting,
              viewerControlRef.current,
            );
        },
        [deleteMarkToBeCreated, isPresenting],
      );

      const {
        startStopPresentHandlerWPresentView,
        updateScrollPositionForReceivers,
        sendFullMessage,
        switchDocument,
      } = useSendMessages({
        passViewingSessionIdToReceivers,
        firstParentTriaBundleFileDetails,
        fileId,
        options,
        viewingFilePrivate,
        viewerControlRef: viewerControlRef,
        setCurrentMouseTool,
        selectedMouseTool,
        deleteMarkToBeCreated,
      });

      const { scrollToHighlight } = useScrollToHighlight({
        viewerControl: viewerControlRef.current,
      });

      const viewerControlMouseDownHandler = useCallback(() => {
        deleteMarkToBeCreated();
      }, [deleteMarkToBeCreated]);

      const viewerControlClickedHandler = useCallback(
        (e: any) => {
          if (e.originalEvent.timeStamp - prevTrippleTimestampRef.current < 250) {
            prevDoubleTimestampRef.current = 0;
            prevTrippleTimestampRef.current = 0;
          } else if (e.originalEvent.timeStamp - prevDoubleTimestampRef.current < 250) {
            prevDoubleTimestampRef.current = 0;
            prevTrippleTimestampRef.current = e.originalEvent.timeStamp;
          } else {
            prevDoubleTimestampRef.current = e.originalEvent.timeStamp;
          }

          if (e.mark && viewerControlRef.current.getAllMarks().includes(e.mark)) {
            if (e.originalEvent.button !== 2) {
              if (e.mark.uid === markToBeCreated?.uid) return;
              if (e.mark.type === Mark.Type.TextHyperlinkAnnotation) {
                openNewBackgroundTab(e.mark.href);
              } else {
                setMarkToBeUpdated(e.mark);
              }
            }
          }
        },
        [markToBeCreated?.uid, setMarkToBeUpdated],
      );

      const markSelectionChangedHandler = useCallback(() => {
        const allSelectedHighlights = viewerControlRef.current.getSelectedMarks();

        if (allSelectedHighlights.length > 1) {
          viewerControlRef.current.deselectMarks([allSelectedHighlights[0]]);
        } else if (allSelectedHighlights.length === 0) {
          setMarkToBeUpdated(undefined);
        } else {
          setMarkToBeUpdated(allSelectedHighlights[0]);
        }
      }, [setMarkToBeUpdated]);

      const setAllTheatreViewerData = useCallback(() => {
        const { scroll, scaleFactor, documentRotation, markObjects = [] } = fileData;

        const rotate = documentRotation - viewerControlRef.current.getPageRotation();

        const decreseIntitializing = () => {
          setTimeout(() => {
            --initializingRef.current;
            if (initializingRef.current === 0) {
              forceUpdate();
            }
          }, 500);
        };

        const setScaleFactor = () => {
          if (viewerControlRef.current.getScaleFactor() !== scaleFactor) {
            viewerControlRef.current.setScaleFactor(
              Math.max(
                Math.min(viewerControlRef.current.getMaxScaleFactor(), scaleFactor),
                viewerControlRef.current.getMinScaleFactor(),
              ),
            );
          }
          decreseIntitializing();
        };

        const setRotation = () => {
          if (rotate) {
            const onRotation = () => {
              decreseIntitializing();
              viewerControlRef.current.off('DocumentRotated', onRotation);
            };
            viewerControlRef.current.on('DocumentRotated', onRotation);
            viewerControlRef.current.rotateDocument(rotate);
          } else {
            decreseIntitializing();
          }
        };

        const _setScroll = () => {
          if (rotate) {
            if (!markBasedScrolling) {
              const centerElementX = getScrollContainer(viewerControlRef.current).clientWidth / 2;
              const centerElementY = getScrollContainer(viewerControlRef.current).clientHeight / 2;
              getScrollContainer(viewerControlRef.current).scrollTo(
                scroll.X * getScrollContainer(viewerControlRef.current).scrollWidth -
                  centerElementX,
                scroll.Y * getScrollContainer(viewerControlRef.current).scrollHeight -
                  centerElementY,
              );
            }

            decreseIntitializing();
          } else {
            if (!markBasedScrolling) {
              setScroll(getScrollContainer(viewerControlRef.current), scroll.X, scroll.Y).then(
                () => {
                  decreseIntitializing();
                },
              );
            } else {
              decreseIntitializing();
            }
          }
        };

        const applyHighlights = () => {
          try {
            const existingMarks = viewerControlRef.current.getAllMarks();

            const markObjectsToDeleteUids = markObjects
              .filter(
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                ({ uid, ...rest }: any) =>
                  Object.keys(rest).length === 0 || rest?.data?.visible === 'false',
              )
              .map((x: any) => x.uid);
            const markObjectsToDelete = existingMarks.filter(({ uid }: any) =>
              markObjectsToDeleteUids.includes(uid),
            );
            // delete some marks
            if (markObjectsToDelete && markObjectsToDelete.length > 0) {
              viewerControlRef.current.deleteMarks(markObjectsToDelete);
            }

            const existingMarksUids = existingMarks.map((x: any) => x.uid);
            const newMarksObjects = markObjects.filter(
              ({ uid, ...rest }: any) =>
                Object.keys(rest).length > 0 && !existingMarksUids.includes(uid),
            );

            // create some new marks
            const newMarks =
              newMarksObjects.length > 0 &&
              viewerControlRef.current.deserializeMarks(newMarksObjects);

            const onlyPresentMarks = newMarks
              ? newMarks.filter(
                  (mark: any) =>
                    mark.type !== SCROLL_MARK_TYPE && mark.type !== PAGENUMBER_MARK_TYPE,
                )
              : [];
            const scrollMarks = newMarks
              ? newMarks.filter((mark: any) => mark.type === SCROLL_MARK_TYPE)
              : [];

            // last one needs not to disappear
            if (onlyPresentMarks.length > 0) {
              if (existingMarks) {
                existingMarks.forEach((mark: any) => {
                  if (
                    mark.fillColor === PRESENT_COLOR &&
                    mark.interactionMode === Mark.InteractionMode.SelectionDisabled &&
                    ((mark.opacity && mark.opacity === 150) ||
                      (typeof mark.opacity === 'undefined' && getAlpha(mark.fillColor) === 255))
                  ) {
                    makeMarkDisappear(mark);
                  }
                });
              }

              onlyPresentMarks
                .slice(0, -1)
                .map(
                  (mark: any) =>
                    mark.interactionMode === Mark.InteractionMode.SelectionDisabled &&
                    makeMarkDisappear(mark),
                );
            }

            if (scrollMarks.length > 0) {
              new Promise<void>((r) => {
                if (scrollMarks.length > 0) {
                  try {
                    const scrollMark = scrollMarks[scrollMarks.length - 1];

                    const scrollContainer = getScrollContainer(viewerControlRef.current);

                    viewerControlRef.current
                      .requestPageAttributes(scrollMark.pageNumber)
                      .then((attributes: any) => {
                        const pageWidth = attributes.width;

                        viewerControlRef.current
                          .scrollToAsync({
                            pageNumber: scrollMark.pageNumber,
                            x:
                              (scrollContainer.scrollWidth - pageWidth) / 2 +
                              scrollMark.boundingRectangle.x,
                            y: scrollMark.boundingRectangle.y,
                          })
                          .then(() => {
                            viewerControlRef.current.deleteMarks([
                              scrollMarks[scrollMarks.length - 1],
                            ]);
                          });
                      });
                  } catch (e) {
                    logger.ERROR(e);
                  } finally {
                    r();
                  }
                } else {
                  r();
                }
              }).then(() => {
                decreseIntitializing();
              });
            } else {
              decreseIntitializing();
            }
          } catch (e) {
            logger.ERROR(e);
            decreseIntitializing();
          }
        };

        initializingRef.current = removeInput ? 3 : 4; //it needs to reach 0 when all is completed
        if (!removeInput) {
          setScaleFactor();
        }
        setRotation();
        _setScroll();
        applyHighlights();
      }, [fileData, forceUpdate, markBasedScrolling, removeInput]);

      const updateScroll = debounce(() => updateScrollPositionForReceivers(), 500);

      const dontUseScrollHandlerRef = useRef(false);

      const scrollHandler = useCallback(() => {
        if (dontUseScrollHandlerRef.current) return;

        updateScroll();
      }, [updateScroll]);

      const scaleChangedHandler = useCallback(
        ({ scaleFactor }: any) => {
          try {
            setTimeout(() => {
              updateScrollPositionForReceivers({
                scaleFactor,
              });
              // this handler get's removed when changing scale
              dontUseScrollHandlerRef.current = false;
            }, 0);
          } catch (e) {
            logger.ERROR(e);
          }
        },
        [updateScrollPositionForReceivers],
      );

      const documentRotatedHandler = useCallback(() => {
        setTimeout(() => {
          updateScrollPositionForReceivers(getDocumentRotationInfo(viewerControlRef.current));
          // this handler get's removed when rotating document
          dontUseScrollHandlerRef.current = false;
        }, 0);
      }, [updateScrollPositionForReceivers]);

      const [highlights, setHighlights] = useState([]);
      const filterHighlightsCancelablePromise = useRef<any>(null);

      const handlePageLoadFailed = useCallback(
        (e: any) => {
          logger.ERROR(e);
          if (e.statusCode === 403) {
            fetchViewingSessionIdHandler().then((viewerOptions: any) => {
              if (isPresenting) {
                sendFullMessage(viewerOptions);
              }
            });
          }
          Logger.ERROR(e);
        },
        [fetchViewingSessionIdHandler, isPresenting, sendFullMessage],
      );

      const handlePageChangedAnnotations = debounce(
        useCallback(
          ({ pageNumber, reset }: any) => {
            filterHighlightsCancelablePromise.current?.cancel();
            filterHighlightsCancelablePromise.current = makeCancelable(
              new Promise((resolve) => {
                const highlightsToShow = Array.from(
                  new Set([
                    ...(reset ? [] : highlights),
                    ...allHighlights.filter(({ annotation }: any) => {
                      return [Math.max(pageNumber - 1, 0), pageNumber, pageNumber + 1].includes(
                        annotation.pageNumber,
                      );
                    }),
                  ]),
                ) as any;
                resolve(highlightsToShow);
              }).then((highlightsToShow: any) => {
                if (highlightsToShow.length !== highlights.length) setHighlights(highlightsToShow);
              }),
            );
            filterHighlightsCancelablePromise.current.promise.then(() => {});

            toolboxRef.current?.pageNumberChangedHandler({
              pageNumber,
            });
          },
          [allHighlights, highlights],
        ),
        250,
      );

      const shouldShowGlobalPaging =
        showGlobalPaging ||
        (fileData && fileData.showGlobalPaging) ||
        Object.keys(queryParam).includes('startPage') ||
        isGlobalPageLookup ||
        (firstParentTriaBundleFileDetails &&
          Object.keys(firstParentTriaBundleFileDetails).length > 0) ||
        (isPresentModePage &&
          currentFileMetaData &&
          Object.keys(currentFileMetaData).includes('folderId')) ||
        isQueryParamMatchingAnyFolder;

      const addPageNumberMark = useMemo(
        () =>
          debounce((pageNumber: number) => {
            pauseCreateMarkHandlerRef2.current = true;

            try {
              if (showBundleTabPageWatermark && bundleTabPageMarkRef.current)
                viewerControlRef.current.deleteMarks([bundleTabPageMarkRef.current]);
              if (showExternIdPageWatermark && externIdPageMarkRef.current)
                viewerControlRef.current.deleteMarks([externIdPageMarkRef.current]);
              if (
                (showGlobalPagePageWatermark || showPrefixPlusGlobalPageWatermark) &&
                globalPageMarkRef.current
              )
                viewerControlRef.current.deleteMarks([globalPageMarkRef.current]);
            } catch {
              // swallow issue
            }

            const promise = viewerControlRef.current.requestPageAttributes(pageNumber);

            promise
              .then(
                function (pageAttributes: { width: number; height: number }) {
                  try {
                    const currentFileMetaDataValue = currentFileMetaDataValueRef.current;

                    if (showBundleTabPageWatermark && !isTheatreMode && shouldShowGlobalPaging) {
                      const bundleTabNumberMark = viewerControlRef.current.addMark(
                        pageNumber,
                        Mark.Type.TextRedaction,
                      );
                      bundleTabNumberMark.setRectangle({
                        x: 25,
                        y: pageAttributes.height - 25,
                        width: 300,
                        height: 25,
                      });
                      bundleTabNumberMark.interactionMode =
                        Mark.InteractionMode.SelectionDisabled;
                      if (
                        currentFileMetaDataValue &&
                        currentFileMetaDataValue.globalPagePrefix &&
                        currentFileMetaDataValue.tab
                      ) {
                        bundleTabNumberMark.text = `[${currentFileMetaDataValue.globalPagePrefix}/${currentFileMetaDataValue.tab}/${pageNumber}]`;
                      }
                      bundleTabPageMarkRef.current = bundleTabNumberMark;
                    }

                    if (showExternIdPageWatermark && externalId) {
                      const externIdNumberMark = viewerControlRef.current.addMark(
                        pageNumber,
                        Mark.Type.TextRedaction,
                      );
                      const estimatedTextWidth = Math.max(60, externalId.length * 10);
                      const markWidth = Math.min(
                        pageAttributes.width,
                        Math.max(120, estimatedTextWidth + 20),
                      );
                      const markX = Math.max(
                        Math.min(
                          (pageAttributes.width - estimatedTextWidth) / 2,
                          pageAttributes.width - markWidth,
                        ),
                        0,
                      );
                      externIdNumberMark.setRectangle({
                        x: markX,
                        y: pageAttributes.height - 25,
                        width: markWidth,
                        height: 25,
                      });
                      externIdNumberMark.interactionMode = Mark.InteractionMode.SelectionDisabled;
                      externIdNumberMark.text = externalId;
                      externIdPageMarkRef.current = externIdNumberMark;
                    }

                    if (
                      (showGlobalPagePageWatermark || showPrefixPlusGlobalPageWatermark) &&
                      !isTheatreMode &&
                      shouldShowGlobalPaging
                    ) {
                      const globalPageNumberMark = viewerControlRef.current.addMark(
                        pageNumber,
                        Mark.Type.TextRedaction,
                      );
                      globalPageNumberMark.setRectangle({
                        x: pageAttributes.width - 75,
                        y: pageAttributes.height - 25,
                        width: 300,
                        height: 25,
                      });
                      globalPageNumberMark.interactionMode =
                        Mark.InteractionMode.SelectionDisabled;
                      if (currentFileMetaDataValue && currentFileMetaDataValue.globalPagePrefix) {
                        const fullText = `${getGlobalPageText(
                          startPageForGlobalPaging,
                          currentFileMetaDataValue,
                          getStartPageForGlobalPaging(
                            startPageForGlobalPaging || currentFileMetaDataValue.startPage,
                          ) +
                            pageNumber -
                            1,
                        )}`;
                        if (showPrefixPlusGlobalPageWatermark) {
                          globalPageNumberMark.text = fullText; // prefix + number (e.g., A150)
                        } else if (showGlobalPagePageWatermark) {
                          const prefix = currentFileMetaDataValue.globalPagePrefix || '';
                          globalPageNumberMark.text = fullText.startsWith(prefix)
                            ? fullText.substring(prefix.length)
                            : fullText; // number only (e.g., 150)
                        }
                      }
                      globalPageMarkRef.current = globalPageNumberMark;
                    }
                  } catch (e) {
                    console.error(e);
                  }
                },
                function (rejectedReason: string) {
                  console.error(
                    'Unable to add annotations because page attributes promise was rejected, error = ' +
                      rejectedReason,
                  );
                },
              )
              .then(() => {
                pauseCreateMarkHandlerRef2.current = false;
              });
          }, 100),
        [
          externalId,
          isTheatreMode,
          shouldShowGlobalPaging,
          showBundleTabPageWatermark,
          showExternIdPageWatermark,
          showGlobalPagePageWatermark,
          showPrefixPlusGlobalPageWatermark,
          startPageForGlobalPaging,
        ],
      );

      const handlePageChanged = useCallback(({ pageNumber }: any) => {
        toolboxRef.current?.pageNumberChangedHandler({
          pageNumber,
        });

        const shouldShowGlobalWatermarks =
          !isTheatreMode &&
          shouldShowGlobalPaging &&
          (showBundleTabPageWatermark ||
            showGlobalPagePageWatermark ||
            showPrefixPlusGlobalPageWatermark);
        if (!showExternIdPageWatermark && !shouldShowGlobalWatermarks) return;
        addPageNumberMark(pageNumber);
      }, [
        addPageNumberMark,
        isTheatreMode,
        shouldShowGlobalPaging,
        showBundleTabPageWatermark,
        showExternIdPageWatermark,
        showGlobalPagePageWatermark,
        showPrefixPlusGlobalPageWatermark,
      ]);

      useEffect(() => {
        if (!showExternIdPageWatermark || !externalId) return;
        if (!viewerControlRef.current?.pageNumber) return;

        addPageNumberMark(viewerControlRef.current.pageNumber);
      }, [addPageNumberMark, externalId, showExternIdPageWatermark]);

      const zoomHandler = useCallback((type: string) => {
        dontUseScrollHandlerRef.current = true;
        zoom(type, viewerControlRef.current);
      }, []);

      const rotateHandler = (angle: number) => {
        dontUseScrollHandlerRef.current = true;
        viewerControlRef.current.rotateDocument(angle);
      };

      const mouseToolHandler = useCallback(
        (toolType: any) => {
          setSelectedMouseTool(toolType);
          setCurrentMouseTool(toolType);
        },
        [setCurrentMouseTool],
      );

      const applyPendingPageNumber = useCallback(() => {
        const pending = pendingPageNumberRef.current;
        const viewer = viewerControlRef.current as any;
        if (!pending || !viewer || !viewer._cs) return;

        const total = Number(viewer.pageCount);
        const clamped =
          Number.isFinite(total) && total > 0
            ? Math.min(Math.floor(pending), total)
            : Math.floor(pending);

        if (clamped <= 0) {
          return;
        }

        if (viewer.pageNumber === clamped) {
          pendingPageNumberRef.current = null;
          return;
        }

        try {
          viewer.setPageNumber(clamped);
          pendingPageNumberRef.current = null;
        } catch (error) {
          logger.ERROR(error);
        }
      }, []);

      const setPageNumberHandler = (pageNumber: number) => {
        try {
          if (!viewerControlRef.current || !viewerControlRef.current._cs) {
            console.warn('Viewer control or its internal service (_cs) is not ready.');
            // return;
          }
          const totalPageCount = viewerControlRef.current.pageCount;
          viewerControlRef.current.setPageNumber(pageNumber <= totalPageCount ? pageNumber : 1);
        } catch (error) {
          console.error('Error setting page number:', error);
        }
      };

      const createMarksInViewer = useCallback(
        (highlightsToWorkWith: any = highlights, differenceWithHighlights?: any) => {
          const currentMouseTool = selectedMouseTool;
          try {
            mouseToolHandler(MouseTool.Type.Pan);
            pauseCreateMarkHandlerRef2.current = true;

            createAnnotations(
              viewerControlRef.current,
              highlightsToWorkWith,
              userId,
              differenceWithHighlights,
            ).then((newMarks: any) => {
              if (markToBeUpdated) {
                setMarkToBeUpdated(newMarks.find(({ uid }: any) => uid === markToBeUpdated.uid));
              }
            });
          } catch (e) {
            logger.ERROR(e);
          } finally {
            setTimeout(() => {
              setMarksCreatedFirstTime(true);
              mouseToolHandler(currentMouseTool && currentMouseTool);
              pauseCreateMarkHandlerRef2.current = false;
            }, 300); // needed for acusoft to stop triggering events
          }
        },
        [
          highlights,
          markToBeUpdated,
          mouseToolHandler,
          selectedMouseTool,
          setMarkToBeUpdated,
          userId,
        ],
      );

      const setReadyWithTimeout = useCallback(
        () =>
          setTimeout(() => {
            setIsViewerReady((prevState: any) => {
              let newState;
              // 4 stage to make it ready
              if (typeof prevState === 'undefined') {
                newState = null;
              } else if (prevState === null) {
                newState = false;
              } else {
                newState = true;
              }
              if (newState) {
                if (presentQuery) {
                  const docToUse = safeGetJSON(
                    sessionStorage,
                    `${caseId}.currentlyPresenting`,
                    null,
                  );
                  startStopPresentHandlerWPresentView(presentQuery, true, docToUse);
                }

                setSearchFunction(viewerControlRef.current, searchHandler, searchFunctionsHandler);
              }

              return newState;
            });
          }, 0),
        [
          caseId,
          presentQuery,
          searchFunctionsHandler,
          searchHandler,
          startStopPresentHandlerWPresentView,
        ],
      );

      const pageOpeningHandler = () => {
        if (!isTheatreMode) {
          // make document go out a bit, no default zoom
          scaleChangedHandler(ZoomType.Out);
        }

        viewerControlRef.current?.off('PageOpening', pageOpeningHandler);
      };
      const pageDisplayedHandler = () => {
        applyPendingPageNumber();
        setReadyWithTimeout();
        viewerControlRef.current?.off('PageDisplayed', pageDisplayedHandler);
      };

      const viewerReadyHandler = () => {
        setReadyWithTimeout();
        setOverflowHidden();

        viewerControlRef.current.off('ViewerReady', viewerReadyHandler);
      };

      useEffect(() => {
        if (isViewerReady && !viewerReadyNotifiedRef.current) {
          viewerReadyNotifiedRef.current = true;
          if (onViewerReady) onViewerReady();
        }
      }, [isViewerReady, onViewerReady]);

      const pageCountReadyHandler = useCallback(() => {
        updatePerfectScrollbar();
        applyPendingPageNumber();
        setTimeout(() => {
          try {
            handlePageChanged({ pageNumber: viewerControlRef.current.pageNumber });
          } catch {
            // TO-DO HIDING ERROR due to timeout set
          }
        }, 350);

        let count = 0;
        const countAllOfThem = () => {
          ++count;
          if (count === highlights.length || isTheatreMode) {
            viewerControlRef.current.off('MarkCreated', countAllOfThem);
            setTimeout(() => {
              setReadyWithTimeout();
            });
          }
        };
        viewerControlRef.current.on('MarkCreated', countAllOfThem);
        if (highlights.length > 0) {
          prevHightlightsRef.current = highlights;
          createMarksInViewer();
        } else {
          --count;
          countAllOfThem();
        }
      }, [
        createMarksInViewer,
        handlePageChanged,
        highlights,
        isTheatreMode,
        applyPendingPageNumber,
        setReadyWithTimeout,
        updatePerfectScrollbar,
      ]);

      const dispatch = useDispatch();
      const setCurrentSelectedFileInStore = useCallback(
        (file: any) => dispatch(setCurrentSelectedFileAction(file)),
        [dispatch],
      );

      useImperativeHandle(ref, () => ({
        stopPresent: () => {
          if (presentQuery) {
            startStopPresentHandlerWPresentView();
          }
        },
        getViewerControl: () => {
          return viewerControlRef.current;
        },
        getCurrentPageNumber: () => {
          return viewerControlRef.current?.pageNumber;
        },
      }));

      const goToFirstPage = () => viewerControlRef.current.changeToFirstPage();
      const goToLastPage = () => viewerControlRef.current.changeToLastPage();
      const goToPrevPage = () => viewerControlRef.current.changeToPrevPage();
      const goToNextPage = () => viewerControlRef.current.changeToNextPage();

      const baseCurrentFileMetaDataValue = useMemo(() => {
        // Helper function to check if an object has properties
        const hasProperties = (obj: object) => obj && Object.keys(obj).length > 0;

        // Helper function to check if query params include specific keys
        const queryParamsMissing = (...keys: string[]) =>
          keys.some((key) => !Object.keys(queryParam).includes(key));

        // Helper function to merge metadata with bundle details if available
        const mergeWithBundleDetails = (metadata: any) => {
          if (!hasProperties(firstParentTriaBundleFileDetails)) {
            return { ...metadata };
          }

          const merged = { ...metadata };

          Object.entries(firstParentTriaBundleFileDetails).forEach(([key, value]) => {
            const current = (merged as any)[key];
            const isMissing =
              current === undefined ||
              current === null ||
              (typeof current === 'string' && current.trim() === '');

            if (isMissing) {
              (merged as any)[key] = value;
            }
          });

          return merged;
        };

        // Case 4: Use query parameters directly when startPage is present, with globalPagePrefix converted to uppercase
        if (Object.keys(queryParam).includes('startPage')) {
          const processedQueryParams = { ...queryParam };

          // Convert globalPagePrefix to uppercase if it exists
          if (processedQueryParams.globalPagePrefix) {
            processedQueryParams.globalPagePrefix =
              processedQueryParams.globalPagePrefix.toUpperCase();
          }
          return mergeWithBundleDetails({ id: fileId, ...processedQueryParams });
        }

        // Case 1: Current file metadata is available and some query params are missing
        if (
          hasProperties(normalizedFileMetaData) &&
          queryParamsMissing('tab', 'globalPagePrefix')
        ) {
          return mergeWithBundleDetails(normalizedFileMetaData);
        }

        // Case 2: Use file data if available
        if (hasProperties(fileDataDetails || {})) {
          return mergeWithBundleDetails(fileDataDetails);
        }

        // Case 3: When query params don't match any folder, return merged metadata
        if (!isQueryParamMatchingAnyFolder) {
          return mergeWithBundleDetails(normalizedFileMetaData || {});
        }
        // Case 5: Fallback to normalized metadata to avoid undefined
        return normalizedFileMetaData;
      }, [
        normalizedFileMetaData,
        queryParam,
        fileDataDetails,
        isQueryParamMatchingAnyFolder,
        firstParentTriaBundleFileDetails,
        fileId,
      ]);

      const presentModeSearchProps = {
        documents: [],
        isAllDocsWithLongList: false,
        isClickEnterToPresent: false,
        isDocFinderPage: false,
        isPresentModePage: true,
        isPresentModeWithBackEnd: false,
        isSearchModePage: false,
        isReady: true,
        placeholder: 'Search',
        showDeepSearch: true,
        selectedColumns: ['index', 'id', 'name', 'info'],
        useOnlyIdColForPresentMode: false,
      };

      useEffect(() => {
        try {
          if (!isTheatreMode)
            handlePageChangedAnnotations({
              pageNumber: viewerControlRef.current?.pageNumber,
              reset: true,
            });
        } catch (e) {
          logger.ERROR(e);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [allHighlights]); // don't want highlights here, I've just set it to [] to trigger the effect

      useEffect(() => {
        hasBeenPresenting.current = hasBeenPresenting.current || isPresenting;
      }, [isPresenting]);

      useEffect(() => {
        if (isViewerReady) {
          applyPendingPageNumber();
        }
      }, [applyPendingPageNumber, isViewerReady]);

      // mount
      useEffect(() => {
        if (!isPreviewMode) {
          fetchAnnotations({
            file: fileId,
            hearingRoomMode,
            presentView: true,
          });
        }

        const vr = viewerControlRef.current;

        // Unmount
        return () => {
          const newUrlQuery =
            qs.parse(window.location.search && window.location.search.substr(1)) || {};

          if (hasBeenPresenting.current && !newUrlQuery.present) {
            dispatch(actions.leaveAGroup('present'));
          }
          clearAnnotations();
          vr?.destroy();
        };
        // MOUNT
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, []);

      useEffect(() => {
        viewerControlRef.current.on('PageCountReady', pageCountReadyHandler);
        const vr = viewerControlRef.current;

        return () => {
          vr.off('PageCountReady', pageCountReadyHandler);
        };
      }, [pageCountReadyHandler]);

      useEffect(() => {
        if (!isViewerReady) return;

        const clearMarkToBeUpdated = () => {
          setMarkToBeUpdated(undefined);
        };

        viewerControlRef.current.on('MarkSelectionChanged', markSelectionChangedHandler);
        viewerControlRef.current.on('Click', viewerControlClickedHandler);
        viewerControlRef.current.on('MouseDown', viewerControlMouseDownHandler);
        getScrollContainer(viewerControlRef.current)?.addEventListener(
          'mousedown',
          deleteMarkToBeCreated as unknown as any,
        );
        getScrollContainer(viewerControlRef.current)?.addEventListener(
          'mousedown',
          clearMarkToBeUpdated,
        );
        if (!isTheatreMode) {
          // document
          getScrollContainer(viewerControlRef.current)?.addEventListener('scroll', scrollHandler);
          viewerControlRef.current.on('ScaleChanged', scaleChangedHandler);
          viewerControlRef.current.on('DocumentRotated', documentRotatedHandler);
          viewerControlRef.current.on('PageChanged', handlePageChangedAnnotations);
        }
        // when page or scale changes we need to update customToolbox
        viewerControlRef.current.on('PageChanged', handlePageChanged);
        viewerControlRef.current.on('PageLoadFailed', handlePageLoadFailed);

        const vr = viewerControlRef.current;

        return () => {
          vr?.off('MarkSelectionChanged', markSelectionChangedHandler);
          vr?.off('Click', viewerControlClickedHandler);
          vr?.off('MouseDown', viewerControlMouseDownHandler);
          getScrollContainer(vr)?.removeEventListener(
            'mousedown',
            deleteMarkToBeCreated as unknown as any,
          );
          getScrollContainer(vr)?.removeEventListener('mousedown', clearMarkToBeUpdated);
          if (!isTheatreMode) {
            // document
            getScrollContainer(vr)?.removeEventListener('scroll', scrollHandler);
            vr?.off('ScaleChanged', scaleChangedHandler);
            vr?.off('DocumentRotated', documentRotatedHandler);
            vr?.off('PageChanged', handlePageChangedAnnotations);
          }
          // when page or scale changes we need to update customToolbox
          vr?.off('PageChanged', handlePageChanged);
          vr?.off('PageLoadFailed', handlePageLoadFailed);
        };
      }, [
        deleteMarkToBeCreated,
        documentRotatedHandler,
        handlePageChanged,
        handlePageChangedAnnotations,
        handlePageLoadFailed,
        isTheatreMode,
        isViewerReady,
        markSelectionChangedHandler,
        scaleChangedHandler,
        scrollHandler,
        setMarkToBeUpdated,
        viewerControlClickedHandler,
        viewerControlMouseDownHandler,
      ]);

      useEffect(() => {
        if (
          isViewerReady &&
          isTheatreMode &&
          fileData &&
          !pauseTheatreMode &&
          !initializingRef.current
        ) {
          setAllTheatreViewerData();
        }
      }, [fileData, isTheatreMode, isViewerReady, pauseTheatreMode, setAllTheatreViewerData]);

      useEffect(() => {
        if (isViewerReady && !isTheatreMode) {
          if (
            annotationId &&
            prevAnnotationIdRef.current !== annotationId &&
            allHighlights?.length > 0
          ) {
            prevAnnotationIdRef.current = annotationId;
            const annotation = allHighlights.find(
              (highlight: any) => highlight.id === annotationId,
            );

            if (annotation) {
              scrollToHighlight(annotation);
            }
          }
        }
      }, [
        allHighlights,
        isTheatreMode,
        isViewerReady,
        scrollToHighlight,
        annotationId,
        marksCreatedFirstTime,
      ]);

      useEffect(() => {
        if (!isViewerReady) return;

        const highlightIds = highlights.map(({ id }: any) => id);
        const highlightsDiffMap = [] as any;
        const highlightsNewMap = [] as any;

        prevHightlightsRef.current.forEach((oldHighlight: any, index: any) => {
          const idIndex = highlightIds.indexOf(oldHighlight.id);
          if (idIndex > -1) {
            highlightIds[idIndex] = null;
            highlightsDiffMap[index] = highlights[idIndex];
            highlightsNewMap[index] = highlights[idIndex];
          } else {
            highlightsDiffMap[index] = undefined;
            highlightsNewMap[index] = oldHighlight;
          }
        });

        highlightIds.filter(Boolean).forEach((id: any) => {
          const idIndex = highlightIds.indexOf(id);
          if (idIndex > -1) {
            highlightsDiffMap[highlightsDiffMap.length] = highlights[idIndex];
            highlightsNewMap[highlightsNewMap.length] = highlights[idIndex];
          }
        });

        const difference = diff(prevHightlightsRef.current, highlightsDiffMap);

        if (!isEmpty(difference)) {
          createMarksInViewer(highlightsNewMap, difference);
          prevHightlightsRef.current = highlights;
        }
      }, [createMarksInViewer, highlights, isViewerReady]);

      useEffect(() => {
        if (
          presentQuery === 'public' &&
          prevHearingRoomStartedRef.current !== hearingRoomStarted &&
          !hearingRoomStarted
        ) {
          if (
            ['paused', 'started'].includes(
              prevHearingRoomStartedRef.current as unknown as string,
            ) &&
            hearingRoomStarted === 'stopped'
          ) {
            startStopPresentHandlerWPresentView();
          }
          prevHearingRoomStartedRef.current = hearingRoomStarted;
        }
      }, [
        connectionId,
        fetchTheatreDataPending,
        hearingRoomStarted,
        isViewerReady,
        startStopPresentHandlerWPresentView,
        presenterConnectionId,
        presentQuery,
      ]);

      useEffect(() => {
        if (isViewerReady && !isTheatreMode && !hasClickedSummary) {
          zoomHandler(ZoomType.Page);
          setHasClickedSummary(true);
        }
      }, [hasClickedSummary, isTheatreMode, isViewerReady, sideSummaryOpen, zoomHandler]);

      const { chipPageNumber, handleOpenDoc, handleOpenRecentlyPresented, selectedDocument } =
        usePresentView({
          value,
          setValue,
          btpActive,
          currentFileMetaData: baseCurrentFileMetaDataValue,
          recentSearches,
          setRecentSearches,
          caseId,
          startStopPresentHandlerWPresentView,
          switchDocument,
          docsToDisplay,
          requestIdRef,
          setDocsToDisplay,
          setCurrentSelectedFile: setCurrentSelectedFileInStore,
        });

      const currentFileMetaDataValue = useMemo(() => {
        const baseMeta = baseCurrentFileMetaDataValue || {};
        if (!selectedDocument || Object.keys(selectedDocument).length === 0) {
          return baseMeta;
        }

        const bestBundleLoc =
          (selectedDocument.bundleLocations || []).find((loc: any) => {
            const locPrefix = loc?.globalPagePrefix;
            return (
              locPrefix !== undefined &&
              String(locPrefix).toUpperCase() ===
                String(selectedDocument.globalPagePrefix || '').toUpperCase()
            );
          }) || (selectedDocument.bundleLocations || [])[0];

        const normalizePrefix = (value: any) => {
          if (value === undefined || value === null) return undefined;
          return String(value).toUpperCase();
        };

        const hasSelectedDoc = Object.keys(selectedDocument).length > 0;
        const selectedHasPrefixProp = hasSelectedDoc && 'globalPagePrefix' in selectedDocument;
        const bundleHasPrefixProp = bestBundleLoc && 'globalPagePrefix' in bestBundleLoc;

        const resolvedPrefix = normalizePrefix(
          selectedHasPrefixProp
            ? selectedDocument.globalPagePrefix
            : bundleHasPrefixProp
              ? bestBundleLoc?.globalPagePrefix
              : hasSelectedDoc
                ? ''
                : baseMeta.globalPagePrefix,
        );

        const resolvedTab = selectedDocument.tab ?? bestBundleLoc?.tab ?? baseMeta.tab;

        const resolvedStartPage =
          selectedDocument.startPage ?? bestBundleLoc?.startPage ?? baseMeta.startPage;

        const resolvedFolderFileId =
          selectedDocument.folderFileId ?? bestBundleLoc?.folderFileId ?? baseMeta.folderFileId;

        const resolvedFolderId =
          selectedDocument.folderId ?? bestBundleLoc?.folderId ?? baseMeta.folderId;

        const resolvedPageCount = selectedDocument.pageCount ?? baseMeta.pageCount;

        return {
          ...baseMeta,
          ...selectedDocument,
          ...(resolvedPrefix !== undefined ? { globalPagePrefix: resolvedPrefix } : {}),
          ...(resolvedTab ? { tab: resolvedTab } : {}),
          ...(resolvedStartPage ? { startPage: resolvedStartPage } : {}),
          ...(resolvedFolderFileId ? { folderFileId: resolvedFolderFileId } : {}),
          ...(resolvedFolderId ? { folderId: resolvedFolderId } : {}),
          ...(resolvedPageCount ? { pageCount: resolvedPageCount } : {}),
        };
      }, [baseCurrentFileMetaDataValue, selectedDocument]);

      useEffect(() => {
        currentFileMetaDataValueRef.current = currentFileMetaDataValue;
      }, [currentFileMetaDataValue]);

      const {
        externalIdFileId,
        externalIdFolderId,
        selectedDocumentExternalId,
        currentFileExternalId,
        fileDataExternalId,
      } = useMemo(() => {
        const selectedDocumentId = selectedDocument?.id || selectedDocument?.fileId;
        const currentFileId = currentFileMetaDataValue?.id;
        const fileDataId = fileDataDetails?.id || fileDataDetails?.fileId;

        return {
          externalIdFileId: selectedDocumentId || currentFileId || fileId || fileDataId,
          selectedDocumentExternalId: selectedDocument?.externalId,
          currentFileExternalId: currentFileMetaDataValue?.externalId,
          fileDataExternalId: fileDataDetails?.externalId,
          externalIdFolderId:
            currentFileMetaDataValue?.folderId ?? selectedDocument?.folderId ?? currentFolder?.id,
        };
      }, [
        selectedDocument?.id,
        selectedDocument?.fileId,
        selectedDocument?.externalId,
        selectedDocument?.folderId,
        currentFileMetaDataValue?.id,
        currentFileMetaDataValue?.externalId,
        currentFileMetaDataValue?.folderId,
        fileDataDetails?.id,
        fileDataDetails?.fileId,
        fileDataDetails?.externalId,
        fileId,
        currentFolder?.id,
      ]);

      useEffect(() => {
        let isActive = true;
        const cachedExternalId =
          selectedDocumentExternalId ??
          currentFileExternalId ??
          fileDataExternalId ??
          null;

        setExternalId(cachedExternalId);

        if (!externalIdFileId) {
          return () => {
            isActive = false;
          };
        }

        fetchFileDetails({
          fileId: externalIdFileId,
          folderId: externalIdFolderId,
        })
          .then((res: any) => {
            if (isActive) {
              setExternalId(res?.externalId || cachedExternalId || null);
            }
          })
          .catch((error: any) => {
            if (isActive) {
              console.error('Failed to fetch external id for watermark', error);
            }
          });

        return () => {
          isActive = false;
        };
      }, [
        currentFileExternalId,
        externalIdFileId,
        externalIdFolderId,
        fetchFileDetails,
        fileDataExternalId,
        selectedDocumentExternalId,
      ]);

      useEffect(() => {
        const parsePositivePage = (value: any) => {
          const num = Number(value);
          return Number.isFinite(num) && num > 0 ? Math.floor(num) : null;
        };

        const candidates = [
          selectedDocument?.goToPage,
          selectedDocument?.localPageNumber,
          currentFileMetaDataValue?.goToPage,
          currentFileMetaDataValue?.localPageNumber,
        ];

        for (const candidate of candidates) {
          const parsed = parsePositivePage(candidate);
          if (parsed) {
            pendingPageNumberRef.current = parsed;
            if (isViewerReady) {
              applyPendingPageNumber();
            }
            return;
          }
        }
      }, [
        applyPendingPageNumber,
        currentFileMetaDataValue?.goToPage,
        currentFileMetaDataValue?.localPageNumber,
        isViewerReady,
        selectedDocument,
      ]);

      return (
        <>
          <Split
            sizes={[15, 85]}
            minSize={showSidebar ? 100 : 0}
            expandToMin={false}
            gutterSize={10}
            gutterAlign="center"
            snapOffset={30}
            dragInterval={1}
            direction="horizontal"
            cursor="col-resize"
            style={{
              height: '100%',
              width: '100%',
              display: 'flex',
            }}
            className={classNames({ 'no-gutter': !showSidebar })}
          >
            <div
              style={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'auto',
                marginTop: 5,
                paddingLeft: 20,
              }}
            >
              <SearchAllFiles
                search={presentModeSearchProps}
                setDocsToDisplay={setDocsToDisplay}
                docsToDisplay={docsToDisplay}
                handleOpenDoc={handleOpenDoc}
                hasSearch={true}
                clickToSearch={false}
                hasDocFinderFilter={true}
                isBundleTabPage={true}
                setShowDocFinderFilter={() => {}}
                showDocFinderFilter={true}
                style={{
                  display: 'flex',
                  flex: 1,
                  width: '100%',
                  padding: '0.5rem',
                }}
                presentView={true}
                value={value}
                setValue={setValue}
                setSearchError={setSearchError}
                startStopPresentHandlerWPresentView={startStopPresentHandlerWPresentView}
                isSearching={isSearching}
                setIsSearching={setIsSearching}
                viewerControlRef={viewerControlRef}
                recentSearches={recentSearches}
                requestIdRef={requestIdRef}
              />
              {docsToDisplay.length === 0 && value.length !== 0 && !isSearching && (
                <div
                  style={{
                    // put this at in the middle of the screen
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: '1rem',
                    minHeight: '100px',
                    textAlign: 'center',
                    color: '#666',
                  }}
                >
                  <div>
                    <p>{T.translate('case.presentView.noDocumentsFound')}</p>
                    <p style={{ fontSize: '0.9rem' }}>
                      {T.translate('case.presentView.noDocumentsFoundMsg')}
                    </p>
                  </div>

                  {searchError.length > 0 && (
                    <div style={{ color: '#d32f2f' }}>
                      <p>{searchError}</p>
                    </div>
                  )}
                </div>
              )}
              <RecentlyPresented
                docsToDisplay={docsToDisplay}
                handleOpenDoc={handleOpenDoc}
                handleOpenRecentlyPresented={handleOpenRecentlyPresented}
                handleRemoveRecentSearch={handleRemoveRecentSearch}
                setSelectedFileForDownload={setSelectedFileForDownload}
                setShowFileVariantsModal={setShowFileVariantsModal}
                setShowClearAllDocsModal={setShowClearAllDocsModal}
                recentSearches={recentSearches}
                currentFileMetaData={currentFileMetaData}
                currentFileMetaDataValue={currentFileMetaDataValue}
                chipPageNumber={chipPageNumber}
                viewerControlRef={viewerControlRef}
              />
            </div>

            <div
              id="div22"
              style={{
                height: '100%',
                position: 'relative',
                overflow: 'auto',
              }}
            >
              {currentFileMetaDataValue && (
                <>
                  {isTheatreMode && (pauseTheatreMode || hardDisconnectOnlyHearingRoom) && (
                    <PausedBanner
                      unpauseTheatreMode={unpauseTheatreModeHandler}
                      disconnectPauseTheatreMode={disconnectPauseTheatreModeHandler}
                      hardDisconnectOnlyHearingRoom={hardDisconnectOnlyHearingRoom}
                      isDisconnected={isDisconnected}
                    />
                  )}
                  {!removeInput &&
                    !(hardDisconnectOnlyHearingRoom && !isDisconnected) &&
                    isViewerReady &&
                    !isLiveStream &&
                    pageListElementRef.current && (
                      <AcusoftViewerToolbox
                        ref={toolboxRef}
                        viewerContainer={pageListElementRef.current}
                        isTheatreMode={isTheatreMode}
                        presenter={fileData?.presenter}
                        fileId={fileId}
                        presentMode={presentQuery}
                        currentScaleFactor={viewerControlRef?.current?.getScaleFactor()}
                        pageCount={viewerControlRef.current.pageCount}
                        zoom={zoomHandler}
                        rotate={rotateHandler}
                        mouseTool={mouseToolHandler}
                        selectedMouseTool={selectedMouseTool}
                        goToFirstPage={goToFirstPage}
                        goToLastPage={goToLastPage}
                        goToPrevPage={goToPrevPage}
                        goToNextPage={goToNextPage}
                        setPageNumber={setPageNumberHandler}
                        present={startStopPresentHandlerWPresentView}
                        permissions={permissions}
                        privateFile={viewingFilePrivate}
                        showGlobalPaging={shouldShowGlobalPaging}
                        currentFileMetaData={{
                          ...currentFileMetaData,
                          ...currentFileMetaDataValue,
                        }}
                        isPresentModePage={true} // this is so it mimics present mode in the toolbox
                        isPreviewMode={isPreviewMode}
                        startPageForGlobalPaging={startPageForGlobalPaging}
                        userId={userId}
                        isPresentViewer={true}
                        presentViewOverrideHandler={handleOpenDoc}
                        viewerControlRef={viewerControlRef}
                        hidePresentButton={hidePresentButton}
                      />
                    )}
                  <AcusoftViewerContainer
                    removeInput={removeInput || (hardDisconnectOnlyHearingRoom && !isDisconnected)}
                    enableScroll={
                      enableScroll && !(hardDisconnectOnlyHearingRoom && !isDisconnected)
                    }
                    className={className}
                    options={options}
                    viewerReadyHandler={viewerReadyHandler}
                    pageOpeningHandler={pageOpeningHandler}
                    pageDisplayedHandler={pageDisplayedHandler}
                    pageListElementRef={pageListElementRef}
                    viewerControlRef={viewerControlRef}
                  />
                  {isViewerReady && !isPresenting && (
                    <NotionInterface
                      viewerControl={viewerControlRef.current}
                      isPresenting={isPresenting}
                      markToBeCreated={markToBeCreated}
                      markToBeUpdated={markToBeUpdated}
                      highlights={allHighlights}
                      isTheatreMode={isTheatreMode}
                      fileId={fileId}
                      deleteMarkToBeCreated={deleteMarkToBeCreated}
                      setMarkToBeUpdated={setMarkToBeUpdated}
                      setMarkToBeCreated={setMarkToBeCreated}
                      hideTimeoutRef={hideTimeoutRef}
                      pauseCreateMarkHandlerRef={pauseCreateMarkHandlerRef}
                      userId={userId}
                    />
                  )}
                </>
              )}
            </div>
            {showFileVariantsModal && (
              <DownloadComponent
                show={showFileVariantsModal}
                selectedRows={[selectedFileForDownload || {}]}
                handleClose={() => setShowFileVariantsModal(false)}
              />
            )}
          </Split>
          <PresentViewClearSearchPopup
            open={showClearAllDocsModal}
            onClose={() => setShowClearAllDocsModal(false)}
            setRecentSearches={setRecentSearches}
            caseId={caseId}
          />
        </>
      );
    },
  ),
);
